import { useCallback, useEffect, useState } from 'react';
import {
  SdkErrorResponse,
  pushError,
  pushErrorFromAPI,
} from 'src/utilities/notifications';
import { AsyncTaskStatus, useAsyncTask } from 'src/utilities/useAsyncTask';

import { SHORT_SEARCH_DELAY } from 'src/utilities/useSearchHooks';
import { sdk } from 'src/utilities/sdk';
import { IErrorResponse } from 'src/utilities/shared-types';

export type UseCreateChildAccountsProps = {
  onSuccess: () => void;
};

export type UseCreateChildAccountsReturn = {
  mutate: (props: MutateFunctionProps) => Promise<void>;
  isLoading: boolean;
};

type MutateFunctionProps = {
  childAccountNames: string[];
  parentCorpId: string;
};

export const useCreateChildAccounts = ({
  onSuccess,
}: UseCreateChildAccountsProps): UseCreateChildAccountsReturn => {
  const { status: asyncTaskStatus, startAsyncTask } = useAsyncTask();
  const [isLoading, setIsLoading] = useState<boolean>();

  const callCreateChildAccountsApi = ({
    childAccountNames,
    parentCorpId,
  }: MutateFunctionProps) =>
    sdk.http.post(
      `/corp-admin/parent-corp/${parentCorpId}/children`,
      childAccountNames
    );

  const resetState = () => {
    setIsLoading(false);
  };

  const handleError = (error?: IErrorResponse | SdkErrorResponse) => {
    const fallbackMessage =
      'There was a problem creating child accounts. Try again.';

    if (error) {
      pushErrorFromAPI(error, {
        fallbackMessage,
      });
    } else {
      pushError(fallbackMessage);
    }

    resetState();
  };

  const mutate = useCallback<(props: MutateFunctionProps) => Promise<void>>(
    async (props) => {
      try {
        setIsLoading(true);

        await startAsyncTask(() => callCreateChildAccountsApi(props));
      } catch (error) {
        handleError(error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleSuccess = async () => {
    onSuccess();
    resetState();
  };

  const handleAsyncStatus = useCallback(() => {
    switch (asyncTaskStatus) {
      case AsyncTaskStatus.COMPLETE:
        /**
         * The reason for this timeout is that elastic search needs some time to rearrange the indexes
         * after a record is deleted. Without this timeout the search could potentially
         * return outdated results
         */
        setTimeout(() => {
          handleSuccess();
        }, SHORT_SEARCH_DELAY);
        break;

      case AsyncTaskStatus.ERROR:
        handleError();
        break;

      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asyncTaskStatus]);

  useEffect(() => {
    handleAsyncStatus();
  }, [handleAsyncStatus]);

  return {
    mutate,
    isLoading,
  };
};
